import React, { useState } from 'react';
import styled from 'styled-components';
import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import { DraggableProvided } from 'react-beautiful-dnd';

import * as graphql from 'generated/graphql';
import { toRgbString } from 'packages/kakapo-ui-kit/helpers/HexColor';
import SettingsIcon from 'packages/kakapo-ui-kit/assets/icons/SettingsIcon';
import CloseIcon from 'packages/kakapo-ui-kit/assets/icons/CloseIcon';
import DragHamburgerIcon from 'packages/kakapo-ui-kit/assets/icons/DragHamburgerIcon';
import Tooltip from 'packages/kakapo-ui-kit/components/tooltips/Tooltip';

import Competency from './items/Competency';
import FinalScore from './items/FinalScore';
import OpenQuestion from './items/OpenQuestion';
import Paragraph from './items/Paragraph';
import ReviewGoals from './items/ReviewGoals';
import Sentiment from './items/Sentiment';
import Signature from './items/Signature';
import Stars from './items/Stars';
import Title from './items/Title';

import EditModal from './EditModal';
import { pickEvaluationSectionItem } from '../utilities/pickEvaluationSectionItem';
import DevelopmentPlan from './items/DevelopmentPlan';

const Card = styled.div<{ dragging: boolean }>`
	background: ${(props) => props.theme.colors.whiteBlue};
	padding: 20px;
	min-height: 60px;
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlue};

	${(props) =>
		props.dragging &&
		`
	border-radius: 5px;
	box-shadow: 0px 0px 0px 1px rgba(${toRgbString(props.theme.colors.slateBlue)}, 0.1),
			    0px 4px 8px rgba(${toRgbString(props.theme.colors.slateBlue)}, 0.25),
			    0px 32px 64px rgba(${toRgbString(props.theme.colors.slateBlue)}, 0.25);
	border-bottom-color: transparent;
	`}
`;

const EditButton = styled.div`
	cursor: pointer;
	display: flex;
	align-items: center;
	flex-shrink: 0;
	flex-grow: 0;
	cursor: pointer;
	color: ${(props) => props.theme.colors.primary};
	padding: 5px;
	margin: -5px;

	&:hover {
		background: rgba(${(props) => toRgbString(props.theme.colors.primary)}, 0.1);
		border-radius: 4px;

		&:active {
			background: rgba(${(props) => toRgbString(props.theme.colors.primary)}, 0.25);
		}
	}
`;

const Toolbar = styled(Flex)<{ hover: boolean }>`
	opacity: 0.25;
	transition: 0.4s ease-in-out opacity;

	${(props) => (props.hover ? `opacity: 1;` : undefined)}

	svg {
		display: block;
	}
`;

function ItemCardLayout(props: {
	hover: boolean;
	center: boolean;
	toolbar: React.ReactNode;
	children: React.ReactNode;
}) {
	return props.center ? (
		<Flex alignItems="center">
			<Box flexGrow={1} flexShrink={1} style={{ pointerEvents: 'none' }}>
				{props.children}
			</Box>
			<Toolbar alignItems="center" ml={3} flexShrink={0} hover={props.hover}>
				{props.toolbar}
			</Toolbar>
		</Flex>
	) : (
		<>
			<Toolbar
				alignItems="center"
				style={{ float: 'right' }}
				ml={3}
				flexShrink={0}
				hover={props.hover}
			>
				{props.toolbar}
			</Toolbar>
			<Box flexGrow={1} flexShrink={1} style={{ pointerEvents: 'none' }}>
				{props.children}
			</Box>
		</>
	);
}

export default function ItemCard(props: {
	item: graphql.EvaluationSectionItem;
	dragging: boolean;
	provided?: DraggableProvided;
	onUpdate: (id: string, item?: graphql.EvaluationSectionItem) => void;
	onDelete: (id: string) => void;
}) {
	const { t } = useTranslation();
	const [hover, setHover] = useState(false);
	const [edit, setEdit] = useState(false);

	const title = pickEvaluationSectionItem(props.item, {
		competency: t('common.settings.evaluationForms.competency.title'),
		openQuestion: t('common.settings.evaluationForms.openQuestion.title'),
		sentiment: t('common.settings.evaluationForms.sentiment.title'),
		signature: t('common.settings.evaluationForms.signature.title'),
		stars: t('common.settings.evaluationForms.stars.title'),
		title: t('common.settings.evaluationForms.title.title'),
		paragraph: t('common.settings.evaluationForms.paragraph.title'),
		finalScore: t('common.settings.evaluationForms.finalScore.title'),
		reviewGoals: t('common.settings.evaluationForms.reviewGoals.title'),
		developmentPlan: t('common.settings.evaluationForms.developmentPlan.title'),
	});

	return (
		<Card
			dragging={props.dragging}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			{...(!edit && props.provided?.draggableProps)}
			{...(!edit && props.provided?.dragHandleProps)}
			ref={props.provided?.innerRef}
		>
			<ItemCardLayout
				hover={props.dragging || hover || edit}
				center={!!props.item.competency || !!props.item.title}
				toolbar={
					<>
						<Box>
							{edit && (
								<Tooltip wide onBlur={() => setEdit(false)} grow styleContent={false}>
									<EditModal item={props.item} onUpdate={props.onUpdate} />
								</Tooltip>
							)}
							<EditButton onClick={() => setEdit(true)}>
								<Box mr={2}>{title}</Box>
								<SettingsIcon color="primary" />
							</EditButton>
						</Box>
						<Box mx={3}>
							<EditButton onClick={() => props.onDelete(props.item.id)}>
								<CloseIcon color="red" />
							</EditButton>
						</Box>
						<DragHamburgerIcon color="slateBlue" />
					</>
				}
			>
				{props.item.competency && <Competency name="item" item={props.item.competency} />}
				{props.item.openQuestion && <OpenQuestion name="item" item={props.item.openQuestion} />}
				{props.item.paragraph && <Paragraph item={props.item.paragraph} />}
				{props.item.sentiment && <Sentiment name="item" item={props.item.sentiment} />}
				{props.item.signature && <Signature name="item" item={props.item.signature} />}
				{props.item.stars && <Stars name="item" item={props.item.stars} />}
				{props.item.title && <Title item={props.item.title} />}
				{props.item.finalScore && <FinalScore name="item" item={props.item.finalScore} />}
				{props.item.reviewGoals && <ReviewGoals />}
				{props.item.developmentPlan && (
					<DevelopmentPlan name="item" item={props.item.developmentPlan} />
				)}
			</ItemCardLayout>
		</Card>
	);
}
